import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, Package, Truck } from 'lucide-react';
import { Button } from './ui/button';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`);
        
        if (!response.ok) {
          throw new Error('Failed to check payment status');
        }

        const data = await response.json();
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return true; // Stop polling
        } else if (data.status === 'expired') {
          setStatus('expired');
          return true; // Stop polling
        }
        
        return false; // Continue polling
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount >= 4) {
          setStatus('error');
          return true; // Stop polling after max attempts
        }
        return false;
      }
    };

    // Initial check
    pollPaymentStatus().then(shouldStop => {
      if (!shouldStop && pollCount < 5) {
        // Set up polling interval
        const interval = setInterval(async () => {
          setPollCount(prev => prev + 1);
          const shouldStop = await pollPaymentStatus();
          if (shouldStop || pollCount >= 4) {
            clearInterval(interval);
          }
        }, 2000);

        return () => clearInterval(interval);
      }
    });
  }, [sessionId, pollCount]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center">
        {status === 'loading' && (
          <div className="space-y-6">
            <Loader2 className="w-16 h-16 text-cyan-400 animate-spin mx-auto" />
            <h1 className="text-3xl font-bold text-white">Processing Payment</h1>
            <p className="text-gray-400">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-8">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            
            <div>
              <h1 className="text-4xl font-bold text-white mb-4">Payment Successful</h1>
              <p className="text-xl text-gray-400">Thank you for your purchase</p>
            </div>

            {paymentData?.metadata && (
              <div className="bg-slate-900 rounded-xl p-6 text-left space-y-4">
                <div className="flex items-center gap-3 text-cyan-400">
                  <Package className="w-5 h-5" />
                  <span className="font-semibold">{paymentData.metadata.product_name}</span>
                </div>
                
                <div className="flex items-center gap-3 text-gray-400">
                  <Truck className="w-5 h-5" />
                  <span>{paymentData.metadata.shipping_description}</span>
                </div>

                <div className="pt-4 border-t border-slate-700">
                  <p className="text-2xl font-bold text-white">
                    ${(paymentData.amount_total / 100).toLocaleString('en-AU', { minimumFractionDigits: 2 })} AUD
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <p className="text-gray-500 text-sm">
                A confirmation email has been sent to your email address.
                We'll be in touch shortly with shipping details.
              </p>
              
              <Link to="/">
                <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold px-8 py-6 rounded-full">
                  Return to Home
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === 'expired' && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto">
              <Package className="w-12 h-12 text-yellow-500" />
            </div>
            <h1 className="text-3xl font-bold text-white">Session Expired</h1>
            <p className="text-gray-400">Your payment session has expired. Please try again.</p>
            <Link to="/#packages">
              <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold px-8 py-6 rounded-full">
                Back to Products
              </Button>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
              <Package className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="text-3xl font-bold text-white">Something Went Wrong</h1>
            <p className="text-gray-400">
              We couldn't verify your payment. Please contact us if you believe this is an error.
            </p>
            <div className="space-x-4">
              <Link to="/#packages">
                <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold px-8 py-6 rounded-full">
                  Back to Products
                </Button>
              </Link>
              <Link to="/#contact">
                <Button variant="outline" className="border-cyan-500 text-cyan-400 px-8 py-6 rounded-full">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
